import React from "react";
import { format } from "date-fns";

/**
 * Feb 2026 — reusable "Created By · <Name> · <Date>" audit line.
 *
 * Renders nothing if both name and date are missing. Legacy records show
 * "Created By: Legacy Record" (grey). Inactive users are surfaced with the
 * "Inactive User (Original Name)" wording resolved by the backend.
 *
 * Props:
 *   name    — resolved display name (backend provides in `created_by_name`)
 *   status  — "active" | "inactive" | "legacy"
 *   date    — ISO timestamp string
 *   variant — "compact" (single line, small text) | "detail" (label + value)
 *   testid  — optional data-testid for the wrapper
 */
export const CreatedByLine = ({
  name,
  status = "active",
  date,
  variant = "compact",
  testid,
  className = "",
}) => {
  if (!name && !date) return null;

  const isLegacy = status === "legacy";
  const isInactive = status === "inactive";
  const displayName = name || "Legacy Record";
  const nameClass = isLegacy
    ? "text-slate-400 italic"
    : isInactive
    ? "text-amber-600"
    : "text-slate-600";

  let dateLabel = "";
  if (date) {
    try {
      dateLabel = format(new Date(date), "dd MMM yyyy · h:mm a");
    } catch {
      dateLabel = "";
    }
  }

  if (variant === "detail") {
    return (
      <div
        data-testid={testid || "created-by-detail"}
        className={`text-xs text-slate-500 space-y-0.5 ${className}`}
      >
        <div>
          <span className="uppercase tracking-wider text-[10px] text-slate-400 mr-1.5">Created By</span>
          <span className={`font-medium ${nameClass}`}>{displayName}</span>
        </div>
        {dateLabel && (
          <div>
            <span className="uppercase tracking-wider text-[10px] text-slate-400 mr-1.5">Created On</span>
            <span className="text-slate-600">{dateLabel}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testid || "created-by-compact"}
      className={`text-[11px] text-slate-400 flex items-center gap-1.5 ${className}`}
    >
      <span>Created by</span>
      <span className={`font-medium ${nameClass}`}>{displayName}</span>
      {dateLabel && (
        <>
          <span className="text-slate-300">·</span>
          <span>{dateLabel}</span>
        </>
      )}
    </div>
  );
};

export default CreatedByLine;
